<template>
  <a-layout-sider
    :zero-width-trigger-style="{ top:'240px' }"
    :collapsed="state.collapsed"
    theme="light"
    class="navbar"
    :class="{ 'navbar--resizing': isResizing }"
    :width="sidebarWidth"
    collapsible
    breakpoint="lg"
    :collapsed-width="state.collapsedWidth"
    @collapse="onCollapse"
    @breakpoint="onBreakpoint">
    <div
      v-if="!state.collapsed && state.collapsedWidth !== 0"
      class="navbar__resize-handle"
      role="separator"
      aria-label="Ширина боковой панели"
      aria-orientation="vertical"
      :aria-valuenow="sidebarWidth"
      :aria-valuemin="minimumSidebarWidth"
      :aria-valuemax="maximumSidebarWidth"
      tabindex="0"
      title="Потяните, чтобы изменить ширину панели"
      @pointerdown="startResize"
      @keydown="resizeWithKeyboard">
      <span
        class="navbar__resize-grip"
        aria-hidden="true">⋮</span>
    </div>
    <div
      v-if="isXmasTime"
      class="crm-logo--xmas"
      :style="{ padding: '.75rem', height: '64px' }">
      <runaway-logo />
      <label class="snow-check">
        <span>Снег&nbsp;</span>
        <a-checkbox
          :checked="isSnowing"
          @change="setSnowing" />
      </label>
    </div>
    <div
      v-else
      :style="{ padding: '.75rem', height: '64px' }">
      <runaway-logo />
    </div>
    <div style="height: calc(100% - 64px); overflow: hidden auto;">
      <a-menu
        v-model:openKeys="state.openKeys"
        :selected-keys="[path, `pinned:${path}`]"
        :items="menuItems"
        mode="inline" />

      <a-menu
        :selected-keys="[path]"
        mode="inline">
        <a-sub-menu key="USER">
          <template #title>
            <UserOutlined />
            <span>USER</span>
          </template>
          <a-menu-item style="pointer-events: none;">
            Логин: {{ currentUser.name }}
          </a-menu-item>
          <a-menu-item
            v-for="(role, key) in currentUser.roles"
            :key="key"
            style="pointer-events: none;">
            Роль: {{ role.name }}
          </a-menu-item>
          <a-menu-item key="/profile">
            <router-link to="/profile">
              <span>Профиль</span>
            </router-link>
          </a-menu-item>
          <a-menu-item
            key="logOut"
            @click="logOut">
            <ExportOutlined />
            <span>Выход</span>
          </a-menu-item>
        </a-sub-menu>
      </a-menu>

      <div
        v-if="isWaitDurationVisible && !state.collapsed">
        <a-statistic
          :value="formatTime(timeData?.preview)"
          :style="{paddingLeft: '24px', marginBottom: '1rem', marginTop: '1rem'}">
          <template #title>
            <span>Превью</span>
            <a-tooltip placement="right">
              <template #title>
                <span>{{ `Рассчитано за последние ${config.waitDuration} минут` }}</span>
              </template>
              <question-circle-outlined style="margin-left: 5px" />
            </a-tooltip>
          </template>
        </a-statistic>

        <a-statistic
          :value="formatTime(timeData?.result)"
          :style="{paddingLeft: '24px'}">
          <template #title>
            <span>Результат</span>
            <a-tooltip placement="right">
              <template #title>
                <span>{{ `Рассчитано за последние ${config.waitDuration} минут` }}</span>
              </template>
              <question-circle-outlined style="margin-left: 5px" />
            </a-tooltip>
          </template>
        </a-statistic>
      </div>

      <p
        v-if="!state.collapsed"
        style="color: #bfbfbf; padding: 2rem 24px 1rem; margin: 0; cursor: pointer;"
        @click="callEasterEgg">
        {{ appVersion ?? '' }}
      </p>
    </div>
  </a-layout-sider>
</template>

<script setup>
import {
  reactive, computed, ref, h, onMounted, onBeforeUnmount,
} from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { routes } from '@/router';
import { isUserAllowedRoute } from '@/router/Middleware/PermissionMiddleware';
import {
  AreaChartOutlined,
  ThunderboltOutlined,
  SettingOutlined,
  AppstoreOutlined,
  PushpinOutlined,
  PushpinFilled,
  UserOutlined,
  ExportOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons-vue';
import getFlag from '@/helpers/getFlag';
import moment from 'moment';
import { pluralize } from '@/helpers/pluralizeHelper';
import config from '@/config'; // eslint-disable-line import/extensions
import { isXmas } from '@/helpers/isXmas';
import RunawayLogo from '@/Components/Common/RunawayLogo.vue';

// Иконки только у корневых разделов: глубже они лишь загромождают дерево,
// где вложенность и так показывает, что чему принадлежит.
const GROUP_ICONS = {
  Избранное: PushpinOutlined,
  CRM: AreaChartOutlined,
  Сервис: ThunderboltOutlined,
  Администрирование: SettingOutlined,
  Библиотека: AppstoreOutlined,
};

const PINNED_GROUP_TITLE = 'Избранное';
const PINNED_STORAGE_KEY = 'navbar-pinned';

const COLLAPSED_WIDTH = 80;
const COLLAPSED_WIDTH_RESPONSIVE = 0;

const NAVBAR_ROUTES = [
  {
    title: 'CRM',
    children: [
      { title: 'Мои планировки', path: '/crm/my-plans' },
      { title: 'Панель ниндзя', path: '/crm/ninja-board' },
      { title: 'Проверка качества', path: '/crm/reviews' },
      { title: 'Мои начисления', path: '/crm/payroll' },
      { title: 'Начисления сотрудников', path: '/crm/payroll/employees' },
      { title: 'График рабочего времени', path: '/crm/work-schedule' },
      { title: 'Нарезка планировок', path: '/crm/plans/cutting' },
      { title: 'Планировки', path: '/crm/plans' },
      { title: 'Загрузка планировок', path: '/crm/upload' },
      { title: 'Поиск 2.0', path: '/crm/search' },
      { title: 'Апартаменты', path: '/crm/apartments' },
      { title: 'Матрица производства', path: '/crm/production-matrix' },
      { title: 'Ниндзя-статистика', path: '/crm/statistic/ninja' },
      { title: 'Статистика по отдельным ниндзя', path: '/crm/statistic/particularNinja' },
      { title: 'База планировок', path: '/crm/plans-database' },
    ],
  },
  {
    title: 'Сервис',
    children: [
      { title: 'Обработка', path: '/service' },
      { title: 'Планировки', path: '/plans' },
      { title: 'Массовые операции', path: '/batch-actions' },
    ],
  },
  {
    title: 'Администрирование',
    children: [
      { title: 'Управление доступом', path: '/administration/access' },
      {
        title: 'Пользователи',
        children: [
          { title: 'Список', path: '/users/list' },
          { title: 'Регистрация', path: '/users/register' },
        ],
      },
      {
        title: 'Контроль качества',
        children: [
          { title: 'Дизайн-документы', path: '/design-files' },
          { title: 'Чеклисты', path: '/quality/checklists' },
          { title: 'Проверки планировок', path: '/quality/guards' },
          { title: 'Категории замечаний', path: '/quality/issue-categories' },
        ],
      },
      {
        title: 'Клиенты и интеграции',
        children: [
          { title: 'Заказчики', path: '/crm/customers' },
          { title: 'Брендинги', path: '/integrations/brandings' },
          { title: 'Вебхуки', path: '/integrations/webhooks' },
        ],
      },
    ],
  },
  {
    title: 'Библиотека',
    children: [
      {
        title: 'SVG мебель', path: '/library/furniture/svg',
      },
      {
        title: 'Unreal мебель',
        children: [
          { title: 'Объекты', path: '/library/furniture/unreal/objects' },
          {
            title: 'Теги',
            children: [
              { title: 'Список', path: '/library/furniture/unreal/tags' },
              { title: 'Категории', path: '/library/furniture/unreal/tags/categories' },
            ],
          },
          { title: 'Группы', path: '/library/furniture/unreal/groups' },
        ],
      },
      {
        title: 'Материалы',
        children: [
          { title: 'Объекты', path: '/library/materials/objects' },
          { title: 'Теги', path: '/library/materials/tags' },
        ],
      },
      {
        title: 'Апертуры',
        children: [
          { title: 'Объекты', path: '/library/apertures/objects' },
          { title: 'Теги', path: '/library/apertures/tags' },
        ],
      },
      {
        title: 'Молдинги',
        children: [
          { title: 'Объекты', path: '/library/moldings/objects' },
          { title: 'Теги', path: '/library/moldings/tags' },
        ],
      },
      {
        title: 'Стили',
        children: [
          { title: 'Список', path: '/library/styles/list' },
          { title: 'Пресеты', path: '/library/styles/presets' },
        ],
      },
      { title: 'Типы комнат', path: '/library/room-types' },
      { title: 'Окружения', path: '/library/environments' },
    ],
  },
];

// Три сервисные группы читают один и тот же список видимых пунктов —
// и в конфиге, и в переменных окружения он остался общим.
const NAVBAR_GROUP_CONFIG_KEYS = {
  CRM: 'crm',
  Сервис: 'service',
  Администрирование: 'service',
  Библиотека: 'service',
};

const NAVBAR_VISIBLE_TABS_ENV_KEYS = {
  CRM: 'VUE_APP_CRM_NAVBAR_VISIBLE_TABS',
  Сервис: 'VUE_APP_SERVICE_NAVBAR_VISIBLE_TABS',
  Администрирование: 'VUE_APP_SERVICE_NAVBAR_VISIBLE_TABS',
  Библиотека: 'VUE_APP_SERVICE_NAVBAR_VISIBLE_TABS',
};

const normalizeNavbarTab = (value) => value.trim().toLowerCase();

const parseNavbarVisibleTabs = (value) => {
  if (!value) return null;

  const tabs = (Array.isArray(value) ? value : value.split(','))
    .map((tab) => normalizeNavbarTab(String(tab)))
    .filter(Boolean);

  if (tabs.length === 0 || tabs.includes('*')) return null;

  // Массив, а не множество: порядок значений задаёт порядок пунктов в меню.
  return tabs;
};

const getNavbarVisibleTabs = (groupTitle) => parseNavbarVisibleTabs(
  config.navbarVisibleTabs?.[NAVBAR_GROUP_CONFIG_KEYS[groupTitle]]
    ?? process.env[NAVBAR_VISIBLE_TABS_ENV_KEYS[groupTitle]],
);

const navbarVisibleTabs = {
  CRM: getNavbarVisibleTabs('CRM'),
  Сервис: getNavbarVisibleTabs('Сервис'),
  Администрирование: getNavbarVisibleTabs('Администрирование'),
  Библиотека: getNavbarVisibleTabs('Библиотека'),
};

const navbarRouteEnvIndex = (groupTitle, navbarRoute) => {
  const visibleTabs = navbarVisibleTabs[groupTitle];

  if (!visibleTabs) return -1;

  const byPath = visibleTabs.indexOf(normalizeNavbarTab(navbarRoute.path ?? ''));

  return byPath === -1 ? visibleTabs.indexOf(normalizeNavbarTab(navbarRoute.title)) : byPath;
};

const isNavbarRouteAllowedByEnv = (groupTitle, navbarRoute) => (
  !navbarVisibleTabs[groupTitle] || navbarRouteEnvIndex(groupTitle, navbarRoute) !== -1
);

const router = useRouter();
const route = useRoute();
const store = useStore();

const path = computed(() => route.path);

const isLocalCollapsed = Boolean(localStorage.getItem('collapsed'));

const isCustomersVisible = getFlag('isCustomersVisible');
const isPlanSearchVisible = getFlag('isPlanSearchVisible');
const isWaitDurationVisible = getFlag('isWaitDurationVisible');
const isUnrealAdminPanelVisible = getFlag('isUnrealAdminPanelVisible');
const isGuardsVisible = getFlag('isGuardsVisible');
const isBrandingVisible = getFlag('isBrandingVisible');
const isEnvironmentVisible = getFlag('isEnvironmentVisible');
const isQualityReviewPanelsVisible = getFlag('isQualityReviewPanelsVisible');

const timeData = ref(null);

const state = reactive({
  openKeys: isLocalCollapsed
    ? []
    : [PINNED_GROUP_TITLE, 'CRM', 'Сервис', 'Администрирование', 'Библиотека', 'USER'],
  collapsed: isLocalCollapsed,
  collapsedWidth: COLLAPSED_WIDTH,
});

const WIDTH_STORAGE_KEY = 'navbar-width';
const viewportWidth = ref(window.innerWidth);
const maximumSidebarWidth = computed(() => (viewportWidth.value >= 992
  ? Math.floor(viewportWidth.value / 4) : 250));
const minimumSidebarWidth = computed(() => Math.min(220, maximumSidebarWidth.value));
const clampSidebarWidth = (width) => Math.min(maximumSidebarWidth.value, Math.max(minimumSidebarWidth.value, width));
const readSidebarWidth = () => {
  try {
    const width = Number(window.localStorage.getItem(WIDTH_STORAGE_KEY));
    return Number.isFinite(width) && width > 0 ? width : 250;
  } catch (error) {
    return 250;
  }
};
const preferredSidebarWidth = ref(readSidebarWidth());
const sidebarWidth = computed(() => clampSidebarWidth(preferredSidebarWidth.value));
const isResizing = ref(false);
let resizeStartX = 0;
let resizeStartWidth = 0;
const saveSidebarWidth = () => {
  try {
    window.localStorage.setItem(WIDTH_STORAGE_KEY, String(preferredSidebarWidth.value));
  } catch (error) {
    // При недоступном хранилище ширина сохраняется только до перезагрузки.
  }
};
const moveResize = (event) => {
  preferredSidebarWidth.value = clampSidebarWidth(
    resizeStartWidth + event.clientX - resizeStartX,
  );
};
const stopResize = () => {
  if (isResizing.value) saveSidebarWidth();
  isResizing.value = false;
  window.removeEventListener('pointermove', moveResize);
  window.removeEventListener('pointerup', stopResize);
  window.removeEventListener('pointercancel', stopResize);
};
const startResize = (event) => {
  if (event.button !== 0) return;
  event.preventDefault();
  resizeStartX = event.clientX;
  resizeStartWidth = sidebarWidth.value;
  isResizing.value = true;
  window.addEventListener('pointermove', moveResize);
  window.addEventListener('pointerup', stopResize);
  window.addEventListener('pointercancel', stopResize);
};
const resizeWithKeyboard = (event) => {
  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  event.preventDefault();
  const widths = { Home: minimumSidebarWidth.value, End: maximumSidebarWidth.value };
  preferredSidebarWidth.value = widths[event.key]
    ?? clampSidebarWidth(sidebarWidth.value + (event.key === 'ArrowRight' ? 10 : -10));
  saveSidebarWidth();
};
const updateViewportWidth = () => { viewportWidth.value = window.innerWidth; };
onMounted(() => window.addEventListener('resize', updateViewportWidth));
onBeforeUnmount(() => {
  stopResize();
  window.removeEventListener('resize', updateViewportWidth);
});

const onCollapse = (isCollapsed, type) => {
  if (type !== 'clickTrigger') return;

  state.collapsed = isCollapsed;

  if (isCollapsed) {
    localStorage.setItem('collapsed', true);
    return;
  }

  localStorage.removeItem('collapsed');
};

const onBreakpoint = (isLessThanBreakpoint) => (isLessThanBreakpoint
  ? state.collapsedWidth = COLLAPSED_WIDTH_RESPONSIVE
  : state.collapsedWidth = COLLAPSED_WIDTH);

const currentUser = computed(() => store.state.auth.currentUser);

const isLeafAllowed = (groupTitle, navbarRoute) => {
  const currentRoute = routes.find((globalRoute) => globalRoute.path === navbarRoute.path);
  return Boolean(currentRoute && isUserAllowedRoute(currentUser.value, currentRoute.meta));
};

// Дерево меню глубже двух уровней, поэтому фильтруем рекурсивно
// и выбрасываем ветки, у которых не осталось ни одного доступного пункта.
// Вес узла — минимальный индекс его пунктов в списке из env: он же задаёт
// порядок, так что перечислением в env можно и скрыть пункт, и переставить.
const NO_ENV_ORDER = Number.MAX_SAFE_INTEGER;

const filterNavbarNodes = (groupTitle, nodes, parentKey) => nodes
  .map((node) => {
    const key = `${parentKey}/${node.path ?? node.title}`;

    if (!node.children) {
      if (!isLeafAllowed(groupTitle, node)) return null;

      const envIndex = navbarRouteEnvIndex(groupTitle, node);

      return {
        ...node,
        key,
        envOrder: envIndex === -1 ? NO_ENV_ORDER : envIndex,
      };
    }

    const children = filterNavbarNodes(groupTitle, node.children, key);

    if (children.length === 0) return null;

    return {
      ...node,
      key,
      children,
      envOrder: Math.min(...children.map((child) => child.envOrder)),
    };
  })
  .filter(Boolean)
  .sort((a, b) => a.envOrder - b.envOrder);

const filteredNavbarRoutes = computed(() => NAVBAR_ROUTES
  .map((routeGroup) => ({
    ...routeGroup,
    key: routeGroup.title,
    icon: GROUP_ICONS[routeGroup.title],
    children: filterNavbarNodes(routeGroup.title, routeGroup.children, routeGroup.title),
  }))
  .filter(({ children }) => children.length > 0));

// Плоский указатель на все доступные пункты: по нему собирается избранное
// и берётся подпись закреплённого пункта вместе с его родителем.
const leafIndex = computed(() => {
  const acc = new Map();

  const walk = (nodes, ancestors) => nodes.forEach((node) => {
    if (node.children) {
      walk(node.children, [...ancestors, node.title]);
      return;
    }

    acc.set(node.path, { ...node, ancestors });
  });

  filteredNavbarRoutes.value.forEach((group) => walk(group.children, [group.title]));

  return acc;
});

const readPinnedPaths = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(PINNED_STORAGE_KEY) ?? '[]');

    return Array.isArray(stored) ? stored.filter((item) => typeof item === 'string') : [];
  } catch (error) {
    return [];
  }
};

const pinnedPaths = ref(readPinnedPaths());

const togglePinned = (pinnedPath) => {
  pinnedPaths.value = pinnedPaths.value.includes(pinnedPath)
    ? pinnedPaths.value.filter((item) => item !== pinnedPath)
    : [...pinnedPaths.value, pinnedPath];

  try {
    window.localStorage.setItem(PINNED_STORAGE_KEY, JSON.stringify(pinnedPaths.value));
  } catch (error) {
    // Приватный режим — закрепления просто не переживут перезагрузку.
  }
};

// Пункты, доступные для закрепления: те, что ещё существуют и видны этому
// пользователю. Порядок — тот, в котором их закрепляли.
const pinnedLeaves = computed(() => pinnedPaths.value
  .map((pinnedPath) => leafIndex.value.get(pinnedPath))
  .filter(Boolean));

const pinnedLabel = (node) => (node.ancestors[1]
  ? `${node.ancestors[1]} / ${node.title}` : node.title);

const leafLabel = (node, isPinned) => h('span', { class: 'nav-leaf' }, [
  h(RouterLink, { to: node.path, class: 'nav-leaf__link', title: isPinned ? pinnedLabel(node) : node.title }, () => [
    node.path === '/crm/search' ? h('span', { class: 'new-badge' }, 'New') : null,
    isPinned ? pinnedLabel(node) : node.title,
  ]),
  h('button', {
    type: 'button',
    class: ['nav-leaf__pin', isPinned ? 'nav-leaf__pin--active' : ''],
    title: isPinned ? 'Убрать из избранного' : 'В избранное',
    'aria-label': isPinned ? 'Убрать из избранного' : 'В избранное',
    // Ссылка пункта меню растянута на всю строку невидимым ::before,
    // поэтому кнопку приходится поднимать над ней и гасить оба события.
    onMousedown: (event) => {
      event.preventDefault();
      event.stopPropagation();
    },
    onClick: (event) => {
      event.preventDefault();
      event.stopPropagation();
      togglePinned(node.path);
    },
  }, [h(isPinned ? PushpinFilled : PushpinOutlined)]),
]);

// ant-design-vue сам разворачивает дерево любой глубины, если отдать ему items.
const toMenuItem = (node) => ({
  key: node.children ? node.key : node.path,
  icon: node.icon ? () => h(node.icon) : undefined,
  label: node.children ? node.title : leafLabel(node, false),
  children: node.children ? node.children.map(toMenuItem) : undefined,
});

const toGroupItem = (group) => ({
  ...toMenuItem(group),
  icon: () => h(GROUP_ICONS[group.title]),
});

const pinnedGroupItem = computed(() => ({
  key: PINNED_GROUP_TITLE,
  icon: () => h(GROUP_ICONS[PINNED_GROUP_TITLE]),
  label: PINNED_GROUP_TITLE,
  children: pinnedLeaves.value.length > 0
    ? pinnedLeaves.value.map((node) => ({
      key: `pinned:${node.path}`,
      label: leafLabel(node, true),
    }))
    : [{
      key: 'pinned:empty',
      disabled: true,
      label: h('span', { class: 'nav-leaf__hint' }, 'Закрепите пункт булавкой'),
    }],
}));

const menuItems = computed(() => [
  pinnedGroupItem.value,
  ...filteredNavbarRoutes.value.map(toGroupItem),
]);

const logOut = () => {
  store.commit('auth/logout');
  router.push('/auth');
};

const appVersion = process.env.VUE_APP_BACKOFFICE_VERSION;

const displayedEnv = config.appEnv === 'dev' ? 'stage' : config.appEnv;

const fetchData = async () => {
  try {
    const params = new URLSearchParams({
      from_time: config.waitDuration,
      environment: displayedEnv,
      unreal: 'prevres',
    });
    const url = `${config.apiControlPanel}/api/v1/datadog/metrics/wait_duration/?${params}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    timeData.value = data.data;
  } catch (error) {
    console.error('Ошибка при получении данных:', error);
    timeData.value = null;
  }
};

const formatTime = (previewData) => {
  if (!previewData || previewData.wait_duration === undefined) return 'Сервис недоступен';
  if (previewData.wait_duration === null) return '0';

  const waitDuration = previewData.wait_duration;
  const duration = moment.duration(waitDuration, 'minutes');

  const minutes = duration.minutes();
  const seconds = duration.seconds();

  const formattedMinutes = minutes > 0 ? `${minutes} ${pluralize(minutes, 'минута', 'минуты', 'минут')}` : '';
  const formattedSeconds = seconds > 0 ? `${seconds} ${pluralize(seconds, 'секунда', 'секунды', 'секунд')}` : '';

  return `${formattedMinutes} ${formattedSeconds}`.trim();
};

if (isWaitDurationVisible) {
  fetchData();
}

const taps = ref(0);

const callEasterEgg = () => {
  taps.value += 1;
  if (taps.value >= 3) {
    taps.value = 0;
    document.body.style.filter = document.body.style.filter ? '' : 'invert(1)';
  }
};

const isXmasTime = isXmas();
const isSnowing = computed(() => store.state.xmas.isSnowing);
const setSnowing = (event) => store.commit('xmas/setSnowing', event.target.checked);

</script>

<style lang="sass">
.ant-layout .ant-layout-sider
  box-shadow: 0 0 25px rgba(0, 0, 0, 0.1)
  height: 100vh

@media (max-width: 992px)
  .ant-layout .ant-layout-sider
    filter: drop-shadow(0 0 15px rgba(0, 0, 0, 0.25))
    box-shadow: 40px 0 30px rgba(0, 0, 0, 0.1)
    position: fixed
    z-index: 6

.ant-statistic-content
  font-size: 14px !important

.navbar__resize-handle
  position: absolute
  top: 0
  right: -4px
  bottom: 0
  width: 8px
  z-index: 10
  cursor: col-resize
  touch-action: none
  background: rgba(0, 0, 0, 0.08)

.navbar__resize-grip
  position: absolute
  top: 50%
  left: 50%
  transform: translate(-50%, -50%)
  display: flex
  align-items: center
  justify-content: center
  width: 16px
  height: 48px
  border: 1px solid #bfbfbf
  border-radius: 6px
  background: #fff
  color: #595959
  font-size: 24px
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12)

.navbar__resize-handle:hover .navbar__resize-grip,
.navbar__resize-handle:focus-visible .navbar__resize-grip,
.navbar--resizing .navbar__resize-grip
  border-color: #1677ff
  color: #1677ff

.navbar__resize-handle:hover,
.navbar__resize-handle:focus-visible
  background: rgba(22, 119, 255, 0.2)

.navbar--resizing
  transition: none !important
  user-select: none

.navbar .ant-menu-title-content
  min-width: 0

.navbar .nav-leaf__pin .anticon
  margin: 0
  min-width: 0
  font-size: 13px

.navbar .nav-leaf__pin .anticon svg
  display: block

.nav-leaf
  display: flex
  align-items: center
  gap: 8px
  width: 100%

.nav-leaf__link
  flex: 1
  min-width: 0
  overflow: hidden
  text-overflow: ellipsis

.nav-leaf__pin
  flex: none
  position: relative
  z-index: 1
  display: flex
  align-items: center
  justify-content: center
  width: 24px
  height: 24px
  margin: 0
  padding: 0
  border: 0
  border-radius: 4px
  background: transparent
  color: rgba(0, 0, 0, 0.45)
  font-size: 13px
  line-height: 1
  cursor: pointer
  opacity: 0
  transition: opacity .2s, color .2s, background-color .2s

.nav-leaf__pin:hover
  color: #1677ff
  background: rgba(0, 0, 0, 0.06)

.ant-menu-item:hover .nav-leaf__pin,
.nav-leaf__pin:focus-visible
  opacity: 1

.nav-leaf__pin--active
  opacity: 1
  color: #1677ff

.nav-leaf__hint
  color: rgba(0, 0, 0, 0.35)
  font-size: 12.5px

.new-badge
  background-color: #1890ff
  color: white
  font-size: 10px
  font-weight: bold
  padding: 2px 6px
  border-radius: 10px
  margin-right: 8px
  vertical-align: middle
  display: inline-block
  line-height: 1

.crm-logo--xmas
  display: block
  position: relative

.crm-logo--xmas::before
  content: ''

  position: absolute
  left: 0
  top: 0
  transform: translate(10%, 5%) rotate(-28deg) scaleX(-1)

  display: block
  width: 32px
  height: 32px
  background-image: url('@/assets/img/xmas/shapka.webp')
  background-size: contain
  background-repeat: no-repeat

.snow-check
  display: flex
  align-items: center
  gap: 4px

  position: fixed
  top: 20px
  right: 20px

  z-index: 100
</style>
